import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
    Alert,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    TextField,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { CLOCK_LABELS, MILESTONE_LABELS, SOURCE_OPTIONS, formatDateTime, fromInputValue, toInputValue } from "./format";

/*
 * Dates and free time rules of one container. A date typed here wins over the
 * carrier feed (the feed's value stays visible). COB needs a reference: a
 * booking or a planned departure is not a confirmed loading.
 */

const EVENT_LABELS = { DISCHARGE: "Tahliye", AVAILABLE: "Çekime hazır", GATE_OUT: "Terminal çıkışı", EMPTY_RETURN: "Boş iade" };

const manualDraft = (container, milestones) =>
    Object.fromEntries(
        milestones.map((key) => {
            const m = container.milestones[key];
            return [key, m?.manual ? { at: toInputValue(m.at), source: m.source, reference: m.reference || "" } : { at: "", source: "MANUAL", reference: "" }];
        })
    );

export default function ContainerStatusDialog({ open, container, meta, suggestedClocks, onClose, onSave }) {
    const [terminal, setTerminal] = useState("");
    const [manual, setManual] = useState({});
    const [deadline, setDeadline] = useState("");
    const [clocks, setClocks] = useState([]);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !container) return;
        setTerminal(container.terminal || "");
        setManual(manualDraft(container, meta.milestones));
        setDeadline("");
        // The facts carry a synthetic clock for the return deadline; only stored clocks are edited here.
        setClocks((container.clocks || []).filter((c) => c.source !== "EMPTY_RETURN_DEADLINE").map((c) => ({ ...c })));
        setError("");
    }, [open, container, meta]);

    if (!container) return null;

    const setField = (key, patch) => setManual((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
    const setClock = (index, patch) => setClocks((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
    const usedTypes = new Set(clocks.map((c) => c.type));

    const save = async () => {
        if (manual.COB?.at && !manual.COB.reference.trim()) {
            setError("COB için yükleme teyidi referansı zorunludur (taşıyıcı mesaj no, belge vb.).");
            return;
        }
        const payload = {
            terminal,
            manual: Object.fromEntries(
                Object.entries(manual).map(([key, v]) => [key, v.at ? { at: fromInputValue(v.at), source: v.source, reference: v.reference } : null])
            ),
            clocks: clocks.map((c) => ({ ...c, freeDays: c.freeDays === "" ? null : c.freeDays })),
        };
        if (deadline) payload.emptyReturnDeadline = fromInputValue(deadline);
        setSaving(true);
        setError("");
        try {
            await onSave(container.containerNumber, payload);
        } catch (e) {
            setError(e?.errors?.[0]?.detail || "Kaydedilemedi.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{container.containerNumber} · tarihler ve serbest süre</DialogTitle>
            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <TextField fullWidth size="small" label="Terminal" value={terminal} onChange={(e) => setTerminal(e.target.value)} sx={{ mb: 2 }} />

                <MDTypography variant="button" fontWeight="bold">
                    Gerçekleşen tarihler
                </MDTypography>
                <MDTypography variant="caption" color="text" display="block" mb={1}>
                    Boş bırakılan alanlarda taşıyıcı verisi kullanılır. Manuel tarih taşıyıcı verisinin önüne geçer.
                </MDTypography>
                {meta.milestones.map((key) => {
                    const feed = container.milestones[key];
                    const draft = manual[key] || { at: "", source: "MANUAL", reference: "" };
                    return (
                        <Grid container spacing={1} alignItems="center" key={key} sx={{ mb: 1 }}>
                            <Grid item xs={12} sm={2}>
                                <MDTypography variant="caption" fontWeight="bold">
                                    {MILESTONE_LABELS[key]}
                                </MDTypography>
                                {feed && !feed.manual && (
                                    <MDTypography variant="caption" color="text" display="block">
                                        Taşıyıcı: {formatDateTime(feed.at)}
                                    </MDTypography>
                                )}
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="datetime-local"
                                    InputLabelProps={{ shrink: true }}
                                    label="Tarih"
                                    value={draft.at}
                                    onChange={(e) => setField(key, { at: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField select fullWidth size="small" label="Kaynak" value={draft.source} onChange={(e) => setField(key, { source: e.target.value })}>
                                    {SOURCE_OPTIONS.map((o) => (
                                        <MenuItem key={o.value} value={o.value}>
                                            {o.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={key === "COB" ? "Yükleme teyidi referansı *" : "Referans / not"}
                                    value={draft.reference}
                                    onChange={(e) => setField(key, { reference: e.target.value })}
                                />
                            </Grid>
                        </Grid>
                    );
                })}

                <TextField
                    size="small"
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    label="Boş iade son tarihi (taşıyıcıdan)"
                    value={deadline}
                    onChange={(e) => setDeadline(e.target.value)}
                    helperText={container.clocks?.find((c) => c.source === "EMPTY_RETURN_DEADLINE") ? "Kayıtlı bir son tarih var; yenisi girilirse değişir." : ""}
                    sx={{ mt: 1, minWidth: 280 }}
                />

                <Divider sx={{ my: 2 }} />
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <MDTypography variant="button" fontWeight="bold">
                        Serbest süre
                    </MDTypography>
                    <MDBox display="flex" gap={1}>
                        {suggestedClocks
                            .filter((s) => !usedTypes.has(s.type))
                            .map((s) => (
                                <Button key={s.type} size="small" variant="outlined" onClick={() => setClocks((prev) => [...prev, { ...s }])}>
                                    Dosyadan: {CLOCK_LABELS[s.type]} {s.freeDays} gün
                                </Button>
                            ))}
                        <TextField
                            select
                            size="small"
                            label="Süre ekle"
                            value=""
                            onChange={(e) =>
                                setClocks((prev) => [
                                    ...prev,
                                    {
                                        type: e.target.value,
                                        startEvent: meta.clockDefaultStart[e.target.value],
                                        freeDays: "",
                                        dayBasis: "CALENDAR",
                                        countStartDay: true,
                                        lastFreeDay: "",
                                        lastFreeTime: "",
                                        confirmed: false,
                                        source: "MANUAL",
                                    },
                                ])
                            }
                            sx={{ minWidth: 150 }}
                        >
                            {meta.clockTypes
                                .filter((t) => !usedTypes.has(t))
                                .map((t) => (
                                    <MenuItem key={t} value={t}>
                                        {CLOCK_LABELS[t]}
                                    </MenuItem>
                                ))}
                        </TextField>
                    </MDBox>
                </MDBox>
                <MDTypography variant="caption" color="text" display="block" mb={1}>
                    Kural onaylanmadan kalan gün hesaplanmaz. Taşıyıcı/terminal son ücretsiz günü bildirdiyse doğrudan girin; hesaplananın önüne geçer.
                </MDTypography>

                {clocks.map((clock, index) => (
                    <MDBox key={clock.type} p={1.5} mb={1} sx={{ border: "1px solid #e5e7eb", borderRadius: "10px" }}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center">
                            <MDTypography variant="button" fontWeight="bold">
                                {CLOCK_LABELS[clock.type]}
                            </MDTypography>
                            <IconButton size="small" onClick={() => setClocks((prev) => prev.filter((_c, i) => i !== index))}>
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </MDBox>
                        <Grid container spacing={1} mt={0.5}>
                            <Grid item xs={6} sm={3}>
                                <TextField fullWidth size="small" label="Taşıyıcı / terminal" value={clock.party || ""} onChange={(e) => setClock(index, { party: e.target.value })} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField select fullWidth size="small" label="Başlangıç" value={clock.startEvent} onChange={(e) => setClock(index, { startEvent: e.target.value })}>
                                    {meta.clockEvents.map((ev) => (
                                        <MenuItem key={ev} value={ev}>
                                            {EVENT_LABELS[ev]}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={4} sm={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    label="Gün"
                                    value={clock.freeDays ?? ""}
                                    onChange={(e) => setClock(index, { freeDays: e.target.value === "" ? "" : Number(e.target.value) })}
                                />
                            </Grid>
                            <Grid item xs={8} sm={4}>
                                <TextField select fullWidth size="small" label="Gün tipi" value={clock.dayBasis} onChange={(e) => setClock(index, { dayBasis: e.target.value })}>
                                    <MenuItem value="CALENDAR">Takvim günü</MenuItem>
                                    <MenuItem value="WORKING">İş günü</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    label="Son ücretsiz gün (bildirilen)"
                                    value={clock.lastFreeDay || ""}
                                    onChange={(e) => setClock(index, { lastFreeDay: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="time"
                                    InputLabelProps={{ shrink: true }}
                                    label="Son saat"
                                    value={clock.lastFreeTime || ""}
                                    onChange={(e) => setClock(index, { lastFreeTime: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={<Checkbox checked={clock.countStartDay !== false} onChange={(e) => setClock(index, { countStartDay: e.target.checked })} />}
                                    label="Başlangıç günü sayılır"
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={!!clock.confirmed} onChange={(e) => setClock(index, { confirmed: e.target.checked })} />}
                                    label="Kural doğrulandı"
                                />
                            </Grid>
                        </Grid>
                    </MDBox>
                ))}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Vazgeç</Button>
                <Button variant="contained" onClick={save} disabled={saving} sx={{ color: "#fff !important" }}>
                    Kaydet
                </Button>
            </DialogActions>
        </Dialog>
    );
}

ContainerStatusDialog.defaultProps = {
    container: null,
    suggestedClocks: [],
};

ContainerStatusDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    container: PropTypes.object,
    meta: PropTypes.object.isRequired,
    suggestedClocks: PropTypes.arrayOf(PropTypes.object),
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
};
